/*
  Cart drawer — Glow theme
  Handles open/close, Shopify cart AJAX, line-item rendering, qty +/-/remove.

  Public API on window.GlowCart:
    open()            — open the drawer (and refresh contents)
    close()           — close
    refresh()         — refetch /cart.js and re-render
    add(formData)     — POST /cart/add.js, then refresh + open
    updateBadges(n)   — set every [data-cart-count] element to n
*/

(function () {
  const root = document.querySelector('[data-cart-drawer]');
  if (!root) return;

  const overlay = root.querySelector('[data-cart-overlay]');
  const panel = root.querySelector('[data-cart-panel]');
  const closeBtn = root.querySelector('[data-cart-close]');
  const itemsEl = root.querySelector('[data-cart-items]');
  const emptyEl = root.querySelector('[data-cart-empty]');
  const footerEl = root.querySelector('[data-cart-footer]');
  const subtotalEl = root.querySelector('[data-cart-subtotal]');
  const compareEl = root.querySelector('[data-cart-compare]');
  const savingsEl = root.querySelector('[data-cart-savings]');
  const savingsAmtEl = root.querySelector('[data-cart-savings-amount]');
  const loadingEl = root.querySelector('[data-cart-loading]');
  const itemTemplate = document.querySelector('[data-cart-item-template]');

  if (!itemTemplate) {
    console.warn('[cart-drawer] item template not found');
    return;
  }

  let isOpen = false;
  let lastFocused = null;
  let fetchInFlight = null;

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function setBusy(busy) {
    root.setAttribute('aria-busy', busy ? 'true' : 'false');
    loadingEl.classList.toggle('hidden', !busy);
  }

  function updateBadges(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = String(count);
      el.classList.toggle('hidden', count === 0);
    });
  }

  function renderItems(cart) {
    itemsEl.innerHTML = '';
    if (!cart.items || cart.items.length === 0) {
      emptyEl.classList.remove('hidden');
      footerEl.classList.add('hidden');
      return;
    }
    emptyEl.classList.add('hidden');
    footerEl.classList.remove('hidden');

    cart.items.forEach(item => {
      const node = itemTemplate.content.cloneNode(true);
      const li = node.querySelector('li');
      li.dataset.itemKey = item.key;

      const img = node.querySelector('[data-item-img]');
      img.src = item.image || item.featured_image?.url || '';
      img.alt = item.product_title || '';

      node.querySelectorAll('[data-item-link], [data-item-title-link]').forEach(a => {
        a.href = item.url;
      });
      node.querySelector('[data-item-title-link]').textContent = item.product_title || '';

      const variantEl = node.querySelector('[data-item-variant]');
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantEl.textContent = item.variant_title;
      } else {
        variantEl.remove();
      }

      const planEl = node.querySelector('[data-item-plan]');
      const planName = item.selling_plan_allocation?.selling_plan?.name;
      if (planName) {
        planEl.textContent = planName;
        planEl.classList.remove('hidden');
      } else {
        planEl.remove();
      }

      node.querySelector('[data-item-qty]').textContent = String(item.quantity);
      node.querySelector('[data-item-price]').textContent = formatMoney(item.final_line_price);

      // Line-level compare-at strikethrough. Pulled (in order of preference):
      //   1. Shopify line/cart discount   → original_line_price > final_line_price
      //   2. Variant compare-at           → item.compare_at_price > item.price
      //   3. Selling-plan compare-at      → item.selling_plan_allocation.compare_at_price > item.price
      //      (this is where Loop subscriptions stash the compare-at)
      const compareLine = node.querySelector('[data-item-compare]');
      const lineOriginal = item.original_line_price || 0;
      const variantCompareUnit = (item.compare_at_price && item.compare_at_price > item.price)
        ? item.compare_at_price : 0;
      const planCompareUnit = (item.selling_plan_allocation
          && item.selling_plan_allocation.compare_at_price
          && item.selling_plan_allocation.compare_at_price > item.price)
        ? item.selling_plan_allocation.compare_at_price : 0;
      const compareUnit = Math.max(variantCompareUnit, planCompareUnit);
      if (lineOriginal && lineOriginal > item.final_line_price) {
        compareLine.textContent = formatMoney(lineOriginal);
        compareLine.classList.remove('hidden');
      } else if (compareUnit > 0) {
        compareLine.textContent = formatMoney(compareUnit * item.quantity);
        compareLine.classList.remove('hidden');
      }

      itemsEl.appendChild(node);
    });

    // Subtotal + strikethrough original + savings line.
    // The compare-at total is the larger of (a) cart.original_total_price — which
    // only differs from total_price when a Shopify line/cart discount is applied —
    // and (b) per-item compare_at_price × quantity summed across the cart. This
    // ensures the strikethrough shows when variants have admin-side compare-at
    // pricing, even if no discount code or automatic discount is active.
    subtotalEl.textContent = formatMoney(cart.total_price);
    let compareAtTotal = 0;
    cart.items.forEach(it => {
      const lineOriginal = it.original_line_price || (it.price * it.quantity);
      const variantCompareUnit = (it.compare_at_price && it.compare_at_price > it.price)
        ? it.compare_at_price : 0;
      const planCompareUnit = (it.selling_plan_allocation
          && it.selling_plan_allocation.compare_at_price
          && it.selling_plan_allocation.compare_at_price > it.price)
        ? it.selling_plan_allocation.compare_at_price : 0;
      const compareUnit = Math.max(variantCompareUnit, planCompareUnit);
      const compareLine = compareUnit * it.quantity;
      compareAtTotal += Math.max(lineOriginal, compareLine);
    });
    if (compareAtTotal > cart.total_price) {
      compareEl.textContent = formatMoney(compareAtTotal);
      compareEl.classList.remove('hidden');
      savingsAmtEl.textContent = formatMoney(compareAtTotal - cart.total_price);
      savingsEl.classList.remove('hidden');
    } else {
      compareEl.classList.add('hidden');
      savingsEl.classList.add('hidden');
    }
  }

  async function fetchCart() {
    if (fetchInFlight) return fetchInFlight;
    setBusy(true);
    fetchInFlight = fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(r => r.json())
      .then(cart => {
        renderItems(cart);
        updateBadges(cart.item_count);
        return cart;
      })
      .catch(err => {
        console.error('[cart-drawer] fetch /cart.js failed', err);
      })
      .finally(() => {
        setBusy(false);
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  async function changeQuantity(key, qty) {
    setBusy(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      const cart = await res.json();
      renderItems(cart);
      updateBadges(cart.item_count);
    } catch (err) {
      console.error('[cart-drawer] change failed', err);
    } finally {
      setBusy(false);
    }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocused = document.activeElement;
    root.setAttribute('data-state', 'open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    fetchCart();
    setTimeout(() => closeBtn?.focus(), 200);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.removeAttribute('data-state');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  async function add(formData) {
    setBusy(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('[cart-drawer] add failed', err);
        alert(err.description || 'Could not add item to cart.');
        return;
      }
      await fetchCart();
      open();
    } catch (err) {
      console.error('[cart-drawer] add error', err);
    } finally {
      setBusy(false);
    }
  }

  // ----------- Event wiring -----------

  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && isOpen) close();
  });

  // Cart icons in header / anywhere
  document.querySelectorAll('[data-cart-icon]').forEach(el => {
    el.addEventListener('click', e => {
      e.preventDefault();
      open();
    });
  });

  // Intercept any Add-to-cart form on the page
  document.addEventListener('submit', e => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.endsWith('/cart/add')) return;
    // Skip if explicitly opted out
    if (form.hasAttribute('data-cart-no-drawer')) return;
    e.preventDefault();
    add(new FormData(form));
  });

  // Delegated qty / remove handlers on items list
  itemsEl.addEventListener('click', e => {
    const li = e.target.closest('li[data-item-key]');
    if (!li) return;
    const key = li.dataset.itemKey;
    const qtyEl = li.querySelector('[data-item-qty]');
    const currentQty = parseInt(qtyEl.textContent, 10) || 0;

    if (e.target.closest('[data-item-decrement]')) {
      changeQuantity(key, Math.max(0, currentQty - 1));
    } else if (e.target.closest('[data-item-increment]')) {
      changeQuantity(key, currentQty + 1);
    } else if (e.target.closest('[data-item-remove]')) {
      changeQuantity(key, 0);
    }
  });

  // Initial cart fetch (so badges are accurate on page load)
  fetchCart();

  // Expose for app integrations (Kaching, Loop, etc. may want to call us)
  window.GlowCart = { open, close, refresh: fetchCart, add, updateBadges };

  // Some apps fire custom events when they add items via their own JS
  // (Kaching uses 'cart:refresh' or similar). Listen for common ones.
  ['cart:refresh', 'cart:item-added', 'cart:updated'].forEach(name => {
    document.addEventListener(name, () => fetchCart());
  });
})();
